import React, { ReactElement, ReactNode } from 'react'
import styles from './OverridableField.module.scss'

enum BadgeMode {
  ManualOverride = 'manual_override',
  TcSet = 'tc_set',
  AutoOverride = 'auto_override',
}

type BadgeStyleKey = 'manualModifiedBadge' | 'autoModifiedBadge'

interface BadgeModeConfig {
  message: string
  title: string
  badgeClassName: BadgeStyleKey
  showRevert: boolean
  getOriginalDisplayValue: (
    extDisplay: string | null,
    autoDisplay: string | null,
    hasExternalData: boolean,
    hasAutoValue: boolean
  ) => string | null
}

const BADGE_MODE_CONFIG: Record<BadgeMode, BadgeModeConfig> = {
  [BadgeMode.ManualOverride]: {
    message: 'TC Manual Override',
    title: 'This value overrides data from the external feed',
    badgeClassName: 'manualModifiedBadge',
    showRevert: true,
    getOriginalDisplayValue: (extDisplay, autoDisplay, hasExternalData, hasAutoValue) =>
      hasExternalData ? extDisplay : (hasAutoValue ? autoDisplay : null),
  },
  [BadgeMode.TcSet]: {
    message: 'TC',
    title: 'This value was set manually (no external feed data available)',
    badgeClassName: 'manualModifiedBadge',
    showRevert: false,
    getOriginalDisplayValue: () => null,
  },
  [BadgeMode.AutoOverride]: {
    message: 'TC Auto Override',
    title: 'This value is from TC automatic lifecylce managegment, overriding the external feed',
    badgeClassName: 'autoModifiedBadge',
    showRevert: false,
    getOriginalDisplayValue: (extDisplay) => extDisplay,
  },
}

interface OverridableFieldProps {
  label?: string // Optional: omit for table/inline contexts
  tcValue: string | null
  extValue: string | null
  extDisplayValue?: string | null // Optional: For display (defaults to extValue)
  autoValue?: string | null // Optional: auto-derived value (when set, enables auto/manual override messaging)
  autoDisplayValue?: string | null // Optional: For display when showing auto value (defaults to autoValue)
  onRevert?: () => void
  children: ReactElement
  modifiedClassName?: string // Optional className to add to child when modified
  extraInfo?: ReactNode // Optional: Additional info to display below the field
  compact?: boolean // Optional: use compact layout without fieldGroup wrapper
}

const OverridableField: React.FC<OverridableFieldProps> = ({
  label,
  tcValue,
  extValue,
  extDisplayValue,
  autoValue,
  autoDisplayValue,
  onRevert,
  children,
  modifiedClassName,
  extraInfo,
  compact = false,
}) => {
  const hasExternalData = extValue !== null && extValue !== ''
  const hasTcValue = tcValue !== null && tcValue !== ''
  const hasAutoValue = autoValue !== null && autoValue !== undefined && autoValue !== ''

  const extDisplay = extDisplayValue !== undefined ? extDisplayValue : extValue
  const autoDisplay = autoDisplayValue !== undefined ? autoDisplayValue : autoValue ?? null

  let badgeMode: BadgeMode | null = null
  if (hasTcValue && hasExternalData && tcValue !== extValue) {
    badgeMode = BadgeMode.ManualOverride
  } else if (hasTcValue && !hasExternalData) {
    badgeMode = hasAutoValue ? BadgeMode.ManualOverride : BadgeMode.TcSet
  } else if (!hasTcValue && hasAutoValue) {
    badgeMode = BadgeMode.AutoOverride
  }

  const config = badgeMode !== null ? BADGE_MODE_CONFIG[badgeMode] : null
  const showRevert = config?.showRevert ?? false
  const originalDisplayValue = config
    ? config.getOriginalDisplayValue(extDisplay, autoDisplay, hasExternalData, hasAutoValue)
    : null
  const showOriginalLine = Boolean(originalDisplayValue)

  const childElement = modifiedClassName && config !== null
    ? React.cloneElement(children, {
      className: `${(children.props as any).className || ''} ${modifiedClassName}`.trim()
    } as any)
    : children

  const content = (
    <>
      {label && (
        <label>
          {label} {config !== null && <span className={styles[config.badgeClassName]} title={config.title}>({config.message})</span>}
        </label>
      )}
      <div className={styles.inputWithRevert}>
        {childElement}
        {showRevert && onRevert && (
          <button
            type="button"
            className={styles.revertButton}
            onClick={onRevert}
            title={label ? `Revert ${label} to original value` : 'Revert to original value'}
            aria-label={label ? `Revert ${label} to original value` : 'Revert to original value'}
          >
            ↺
          </button>
        )}
      </div>
      {showOriginalLine && (
        <p className={styles.originalValue}>
          <strong>Original:</strong> {originalDisplayValue}
        </p>
      )}
      {extraInfo && <>{extraInfo}</>}
    </>
  )

  if (compact) {
    return <div className={styles.compactField}>{content}</div>
  }

  return <div className="fieldGroup">{content}</div>
}

export default OverridableField

